// Pure heuristics for the search page's place-suggestion chip: deciding whether a free-text
// query looks like a bare place name and whether a geocoder suggestion actually IS that
// place. Kept free of React/API imports so node --test can exercise them directly
// (usePlaceSuggestion.js wires them to geoSuggest/geoRetrieve).

// Feature types the chip may suggest — cities (Mapbox "place" or the finer "locality"),
// regions and countries. POIs, streets and addresses never nag.
const CHIP_FEATURE_TYPES = new Set(["place", "locality", "region", "country"]);

// Folds a query / place name to its comparable form: diacritics stripped (NFD + drop
// combining marks), lowercased, whitespace collapsed — "KÖLN " and "koln" both → "koln".
export function normalizePlaceQuery(text) {
  return (text ?? "")
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

// A (normalized) query is worth a geocoder probe when it plausibly IS a place name:
// 3–40 chars and at most three words ("new york city" qualifies, sentence-like queries
// never do).
export function isPlaceProbeCandidate(normalizedQuery) {
  if (normalizedQuery.length < 3 || normalizedQuery.length > 40) return false;
  return normalizedQuery.split(" ").length <= 3;
}

// The chip only ever suggests what the user literally typed: the first place-like
// suggestion whose normalized name EQUALS the normalized query. "koln" matches Köln
// (diacritics fold); "berlin consulting" matches nothing — no place carries that name.
export function findPlaceSuggestion(query, suggestions) {
  const normalized = normalizePlaceQuery(query);
  if (!isPlaceProbeCandidate(normalized)) return null;
  return (
    (suggestions ?? []).find(
      (s) => CHIP_FEATURE_TYPES.has(s?.featureType) && normalizePlaceQuery(s.name) === normalized
    ) ?? null
  );
}
